/*
  Sends snippets/review-form.liquid to Judge.me's public review endpoint. Ported from the
  2024 theme's evry-judgeme-reviews.js.

  Keep the body x-www-form-urlencoded, never application/json. Judge.me's API has no route
  for OPTIONS, so it cannot answer a CORS preflight; a JSON body triggers one and the
  browser blocks the request. A form-encoded POST with no custom headers is a CORS "simple
  request": no preflight, and the API's reflected Access-Control-Allow-Origin still lets us
  read the response to report real success or failure.
*/
(() => {
  const ENDPOINT = 'https://api.judge.me/api/v1/reviews';

  /** @param {HTMLFormElement} form */
  const messagesFor = (form) => {
    try {
      return JSON.parse(form.querySelector('[data-review-messages]')?.textContent || '{}');
    } catch {
      return {};
    }
  };

  /**
   * @param {HTMLFormElement} form
   * @param {string} text
   * @param {'error' | 'success' | 'pending' | ''} [kind]
   */
  const setStatus = (form, text, kind = '') => {
    const status = form.querySelector('[data-review-status]');
    if (!status) return;
    status.textContent = text || '';
    status.className = `review-form__status${kind ? ` review-form__status--${kind}` : ''}`;
  };

  /**
   * @param {HTMLFormElement} form
   * @param {string} name
   */
  const value = (form, name) => {
    const field = form.elements.namedItem(name);
    return field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement ? field.value.trim() : '';
  };

  /**
   * @param {HTMLFormElement} form
   * @param {Record<string, string>} messages
   */
  const validate = (form, messages) => {
    if (!form.querySelector('input[name="rating"]:checked')) return messages.error_rating;
    if (!value(form, 'name')) return messages.error_name;
    // Loose on purpose: Judge.me is the authority; this only catches obvious typos.
    const email = value(form, 'email');
    if (!email || email.indexOf('@') < 1 || !email.includes('.')) return messages.error_email;
    if (!value(form, 'body')) return messages.error_body;
    return null;
  };

  /** @param {HTMLFormElement} form */
  const submit = async (form) => {
    const messages = messagesFor(form);
    // The submit button can sit outside the form (the review drawer's footer), tied by `form=`.
    const button =
      form.querySelector('[type="submit"]') ?? (form.id ? document.querySelector(`[type="submit"][form="${form.id}"]`) : null);

    // Honeypot: report success without sending. Telling a bot it failed only makes it retry.
    if (value(form, 'website')) {
      form.dataset.submitted = '';
      setStatus(form, messages.success, 'success');
      return;
    }

    const error = validate(form, messages);
    if (error) {
      setStatus(form, error, 'error');
      return;
    }

    const rating = /** @type {HTMLInputElement} */ (form.querySelector('input[name="rating"]:checked'));
    const body = new URLSearchParams({
      shop_domain: form.dataset.shopDomain || '',
      platform: 'shopify',
      id: form.dataset.productId || '',
      rating: rating.value,
      name: value(form, 'name'),
      email: value(form, 'email'),
      body: value(form, 'body'),
    });
    const title = value(form, 'title');
    if (title) body.append('title', title);

    if (button instanceof HTMLButtonElement) button.disabled = true;
    setStatus(form, messages.submitting, 'pending');

    try {
      const response = await fetch(ENDPOINT, {
        method: 'POST',
        // A CORS-safelisted Content-Type only; anything else forces a preflight.
        headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
        body: body.toString(),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      form.dataset.submitted = '';
      setStatus(form, messages.success, 'success');
    } catch (submitError) {
      if (button instanceof HTMLButtonElement) button.disabled = false;
      setStatus(form, messages.error_generic, 'error');
      console.warn('[review-form] submission failed:', submitError);
    }
  };

  const init = () => {
    for (const form of document.querySelectorAll('form[data-review-form]')) {
      if (!(form instanceof HTMLFormElement) || form.dataset.bound) continue;
      form.dataset.bound = 'true';

      form.addEventListener('submit', (event) => {
        event.preventDefault();
        if (form.dataset.submitted === undefined) submit(form);
      });

      // Clear a validation message as soon as the shopper acts on it.
      form.addEventListener('change', () => {
        if (form.querySelector('.review-form__status--error')) setStatus(form, '');
      });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', init);
})();
